import React, { useEffect, useState, useRef, memo } from "react";
import getJumpPhotos from "../functions/getJumpPhotos";
import "./JumpPhotos.css";

const imgHeight = "125";

// Cache version - increment when URL structure changes to invalidate stale cache
const CACHE_VERSION = "v2";
const CACHE_KEY = `jumpPhotos_${CACHE_VERSION}`;

// Image that shows shimmer placeholder until loaded, then fades in
const FadeInImage = memo(({ src, alt, height, showPlaceholder }) => {
  const [loaded, setLoaded] = useState(false);

  return (
    <div style={{ position: 'relative', height: `${height}px`, flexShrink: 0 }}>
      {/* Shimmer placeholder shown until image loads (only if requested) */}
      {!loaded && showPlaceholder && <div className="photo-ghost" style={{ position: 'absolute', top: 0, left: 0 }} />}
      <img
        src={src}
        alt={alt}
        height={height}
        loading="eager"
        decoding="async"
        onLoad={() => setLoaded(true)}
        style={{
          opacity: loaded ? 1 : 0,
          transition: 'opacity 0.3s ease-in-out'
        }}
      />
    </div>
  );
});

const JumpPhotos = () => {
  const [photos, setPhotos] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showImagePlaceholders, setShowImagePlaceholders] = useState(false);
  const [scrollOffset, setScrollOffset] = useState(0);
  const lastScrollY = useRef(0);
  const cumulativeOffset = useRef(0);
  const trackRef = useRef(null);
  const singleSetWidth = useRef(0);

  useEffect(() => {
    // Clear old cache keys (without version)
    localStorage.removeItem("jumpPhotos");

    // Try to load from localStorage first
    const cachedPhotos = localStorage.getItem(CACHE_KEY);
    if (cachedPhotos) {
      try {
        const parsed = JSON.parse(cachedPhotos);
        if (Array.isArray(parsed) && parsed.length > 0) {
          setPhotos(parsed);
          setIsLoading(false);
          setShowImagePlaceholders(false);
          return;
        }
      } catch (e) {
        // Invalid cache, clear it
        localStorage.removeItem(CACHE_KEY);
      }
    }

    // Fetch from API
    setShowImagePlaceholders(true);
    getJumpPhotos()
      .then((x) => {
        if (Array.isArray(x) && x.length > 0) {
          setPhotos(x);
          localStorage.setItem(CACHE_KEY, JSON.stringify(x));
        }
        setIsLoading(false);
      })
      .catch((err) => {
        console.error("Failed to load jump photos:", err);
        setIsLoading(false);
      });
  }, []);

  // Fewer copies on mobile, more on desktop for wide screens
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
  const copyCount = isMobile ? 2 : 5;

  // Calculate width of one photo set after images load
  useEffect(() => {
    if (photos.length > 0 && trackRef.current) {
      // Wait for images to load, then calculate single set width
      const calculateWidth = () => {
        const track = trackRef.current;
        if (track) {
          const totalWidth = track.scrollWidth;
          singleSetWidth.current = totalWidth / copyCount;
        }
      };
      // Calculate after a short delay to let images load
      setTimeout(calculateWidth, 500);
    }
  }, [photos, copyCount]);

  useEffect(() => {
    const handleScroll = () => {
      // Ignore programmatic scrolls (like page navigation)
      if (window.__programmaticScroll) {
        lastScrollY.current = window.scrollY;
        return;
      }

      const currentScrollY = window.scrollY;
      const delta = currentScrollY - lastScrollY.current;

      // Update photo position based on scroll delta
      cumulativeOffset.current += delta * 2;

      // Wrap around for infinite scroll in both directions
      if (singleSetWidth.current > 0) {
        // Wrap when going too far right (scrolling down)
        if (cumulativeOffset.current > singleSetWidth.current) {
          cumulativeOffset.current = cumulativeOffset.current % singleSetWidth.current;
        }
        // Wrap when going too far left (scrolling up)
        if (cumulativeOffset.current < 0) {
          cumulativeOffset.current = singleSetWidth.current + (cumulativeOffset.current % singleSetWidth.current);
        }
      }

      setScrollOffset(cumulativeOffset.current);
      lastScrollY.current = currentScrollY;
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const duplicatedPhotos = photos.length > 0
    ? Array.from({ length: copyCount }, () => photos).flat()
    : [];

  // Show enough ghost placeholders to fill viewport width
  const ghostCount = 15;
  const ghostPhotos = Array.from({ length: ghostCount }, (_, i) => i);

  return (
    <div className="jump-photos-wrapper">
      <div className="jump-photos-container">
        <div
          ref={trackRef}
          className="photo-track"
          style={{ transform: isLoading ? "translateX(0)" : `translateX(-${scrollOffset}px)` }}
        >
          {isLoading || duplicatedPhotos.length === 0 ? (
            ghostPhotos.map((idx) => (
              <div key={`ghost-${idx}`} className="photo-ghost" />
            ))
          ) : (
            duplicatedPhotos.map((x, idx) => (
              <FadeInImage
                key={"jumpPhoto-" + idx}
                src={x.file.url + `?h=125&q=80`}
                alt={x.title}
                height={imgHeight}
                showPlaceholder={showImagePlaceholders}
              />
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default JumpPhotos;
